import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import toast from 'react-hot-toast';
import {
  QrCode,
  RefreshCw,
  MapPin,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { qrAttendanceAPI } from '../services/api';

const formatResult = (result) =>
  String(result || '-')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const QrAttendance = () => {
  const [settings, setSettings] = useState({
    enabled: false,
    codeTtlSeconds: 60,
    locationLabel: '',
  });
  const [draft, setDraft] = useState({
    codeTtlSeconds: 60,
    locationLabel: '',
  });
  const [code, setCode] = useState(null);
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const refreshTimer = useRef(null);

  const ttl = Math.max(1, Number(settings.codeTtlSeconds) || 60);
  const progress = useMemo(() => {
    if (!code || !settings.enabled) return 0;
    return Math.min(100, Math.max(0, (countdown / ttl) * 100));
  }, [code, countdown, settings.enabled, ttl]);

  const loadSettings = useCallback(async () => {
    const res = await qrAttendanceAPI.getSettings();
    const next = {
      enabled: !!res.data?.enabled,
      codeTtlSeconds: Number(res.data?.codeTtlSeconds) || 60,
      locationLabel: res.data?.locationLabel || '',
    };
    setSettings(next);
    setDraft({
      codeTtlSeconds: next.codeTtlSeconds,
      locationLabel: next.locationLabel,
    });
    return res.data;
  }, []);

  const loadScans = useCallback(async () => {
    try {
      const res = await qrAttendanceAPI.listScans(30);
      setScans(res.data || []);
    } catch {
      // non-blocking
    }
  }, []);

  const refreshCode = useCallback(async () => {
    try {
      setRefreshing(true);
      const res = await qrAttendanceAPI.refreshCode();
      setCode(res.data);
      setCountdown(Number(res.data?.expiresInSeconds) || 0);
    } catch (e) {
      setCode(null);
      toast.error(e.response?.data?.message || 'Failed to refresh QR code');
    } finally {
      setRefreshing(false);
    }
  }, []);

  const ensureActiveCode = useCallback(async () => {
    try {
      const res = await qrAttendanceAPI.getActiveCode();
      if (res.data?.token) {
        setCode(res.data);
        setCountdown(Number(res.data?.expiresInSeconds) || 0);
      } else {
        await refreshCode();
      }
    } catch (e) {
      if (e.response?.status === 403) {
        setCode(null);
        return;
      }
      await refreshCode();
    }
  }, [refreshCode]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const s = await loadSettings();
        await loadScans();
        if (s?.enabled) {
          await ensureActiveCode();
        }
      } catch {
        toast.error('Failed to load QR attendance');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadSettings, loadScans, ensureActiveCode]);

  const activeCodeToken = code?.token || null;

  useEffect(() => {
    if (!settings.enabled || !activeCodeToken) return undefined;

    if (refreshTimer.current) clearInterval(refreshTimer.current);

    refreshTimer.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          void refreshCode();
          void loadScans();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (refreshTimer.current) clearInterval(refreshTimer.current);
    };
  }, [settings.enabled, activeCodeToken, refreshCode, loadScans]);

  const saveSettings = async (patch) => {
    try {
      setSaving(true);
      const next = { ...settings, ...draft, ...patch };
      const res = await qrAttendanceAPI.updateSettings(next);
      const saved = {
        enabled: !!res.data?.enabled,
        codeTtlSeconds: Number(res.data?.codeTtlSeconds) || 60,
        locationLabel: res.data?.locationLabel || '',
      };
      setSettings(saved);
      setDraft({
        codeTtlSeconds: saved.codeTtlSeconds,
        locationLabel: saved.locationLabel,
      });
      toast.success('Settings saved');
      if (saved.enabled) {
        await refreshCode();
      } else {
        setCode(null);
      }
      await loadScans();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[45vh] flex items-center justify-center">
        <div className="flex items-center gap-2 text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-[13px]">Loading QR attendance…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="page-title">QR Attendance</h1>
          <p className="page-subtitle">
            Display a rotating station code for mobile check-in and check-out.
          </p>
        </div>

        <button
          type="button"
          disabled={saving}
          onClick={() => saveSettings({ enabled: !settings.enabled })}
          className="self-start inline-flex items-center gap-2.5 pl-1 pr-3 py-1 rounded-full border border-gray-200 bg-white text-gray-700 disabled:opacity-60"
        >
          <span
            className={`relative w-9 h-5 rounded-full transition-colors ${
              settings.enabled ? 'bg-gray-900' : 'bg-gray-300'
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                settings.enabled ? 'translate-x-4' : 'translate-x-0'
              }`}
            />
          </span>
          <span className="text-[13px] font-medium">
            {settings.enabled ? 'Live' : 'Off'}
          </span>
        </button>
      </div>

      <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-4">
        <section className="panel !p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-[14px] font-semibold text-gray-900">Live code</h2>
              <p className="text-subtle text-[12px]">
                {settings.enabled
                  ? code?.locationLabel || 'Ready for scanning'
                  : 'Turn on to display a station code'}
              </p>
            </div>
            {settings.enabled && (
              <button
                type="button"
                onClick={() => refreshCode()}
                disabled={refreshing}
                className="btn-outline !h-7 !px-2.5 !text-[12px]"
              >
                <RefreshCw className={`w-3 h-3 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            )}
          </div>

          <div className="p-6 md:p-8 flex flex-col items-center justify-center min-h-[320px] bg-gray-50/50">
            {!settings.enabled && (
              <div className="text-center max-w-xs">
                <div className="mx-auto mb-3 w-11 h-11 rounded-lg bg-gray-100 flex items-center justify-center">
                  <QrCode className="w-5 h-5 text-gray-400" />
                </div>
                <p className="text-[14px] font-medium text-gray-900">QR attendance is off</p>
                <p className="text-subtle text-[13px] mt-1">
                  Enable it above to generate a rotating station code.
                </p>
              </div>
            )}

            {settings.enabled && code?.payload && (
              <>
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <QRCodeSVG
                    value={code.payload}
                    size={210}
                    level="M"
                    includeMargin={false}
                    bgColor="#ffffff"
                    fgColor="#111827"
                  />
                </div>

                <div className="mt-5 w-full max-w-[240px]">
                  <div className="flex items-center justify-between text-[13px] mb-1.5">
                    <span className="text-subtle">Refreshes in</span>
                    <span className="font-medium tabular-nums text-gray-900">
                      {countdown}s
                    </span>
                  </div>
                  <div className="h-1 rounded-full bg-gray-200 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gray-800 transition-[width] duration-1000 ease-linear"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              </>
            )}

            {settings.enabled && !code && (
              <button type="button" onClick={() => refreshCode()} className="btn-primary">
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                Generate QR
              </button>
            )}
          </div>
        </section>

        <div className="space-y-4">
          <section className="panel">
            <h2 className="text-[14px] font-semibold text-gray-900 mb-3">Settings</h2>
            <div className="space-y-3">
              <label className="block">
                <span className="text-subtle text-[12px] flex items-center gap-1 mb-1">
                  <MapPin className="w-3 h-3" />
                  Location label
                </span>
                <input
                  type="text"
                  value={draft.locationLabel}
                  onChange={(e) =>
                    setDraft((s) => ({ ...s, locationLabel: e.target.value }))
                  }
                  className="input"
                  placeholder="Main entrance"
                />
              </label>

              <label className="block">
                <span className="text-subtle text-[12px] flex items-center gap-1 mb-1">
                  <Clock className="w-3 h-3" />
                  Code lifetime (seconds)
                </span>
                <input
                  type="number"
                  min={15}
                  max={3600}
                  value={draft.codeTtlSeconds}
                  onChange={(e) =>
                    setDraft((s) => ({
                      ...s,
                      codeTtlSeconds: Number(e.target.value) || 60,
                    }))
                  }
                  className="input"
                />
              </label>

              <button
                type="button"
                disabled={saving}
                onClick={() => saveSettings({})}
                className="btn-primary w-full"
              >
                {saving ? 'Saving…' : 'Save settings'}
              </button>
            </div>
          </section>

          <section className="panel !p-0 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-[14px] font-semibold text-gray-900">Recent scans</h2>
              <button
                type="button"
                onClick={() => loadScans()}
                className="text-[12px] font-medium text-gray-600 hover:text-gray-900"
              >
                Reload
              </button>
            </div>

            {scans.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <p className="text-[13px] font-medium text-gray-800">No scans yet</p>
                <p className="text-subtle text-[12px] mt-0.5">
                  Activity appears here after the first scan.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100 max-h-[300px] overflow-y-auto">
                {scans.map((row) => {
                  const ok = row.result === 'success';
                  return (
                    <li key={row._id} className="px-4 py-2.5 flex items-start gap-2.5">
                      <div className="mt-0.5 w-6 h-6 rounded-full flex items-center justify-center shrink-0 bg-gray-100 text-gray-600">
                        {ok ? (
                          <CheckCircle2 className="w-3.5 h-3.5" />
                        ) : (
                          <AlertTriangle className="w-3.5 h-3.5" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-[13px] font-medium text-gray-900 truncate">
                            {row.employeeId?.name ||
                              row.employeeId?.employeeId ||
                              'Unknown'}
                          </p>
                          <span className="shrink-0 px-1.5 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-600">
                            {formatResult(row.result)}
                          </span>
                        </div>
                        <p className="text-[12px] text-gray-500">
                          {row.scannedAt
                            ? new Date(row.scannedAt).toLocaleString()
                            : '—'}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default QrAttendance;
